import React from 'react'
import PropTypes from 'prop-types'
import { Button, Separator } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'

/**
 * ReviewCardFooter
 *
 * Renders the action buttons for the Review Card. Shows different button layouts based on
 * whether additional people need details:
 *  - Has new people: Shows single "Save and Continue" button.
 *  - No new people: Shows "Attach to Tree" + optional "Add Household Member" + "Close" buttons.
 *
 * @component
 * @param {Object}   props
 * @param {Array}    [props.familyMembersToReview=[]]  Array of new people to review.
 * @param {Function} props.onSave                      Callback fired when user clicks a save button. Receives action: 'close', 'attach', or 'continue'.
 * @param {Function} props.onAddAnotherPerson            Required. Callback fired when user clicks "Add Household Member".
 * @returns {JSX.Element}
 */
const ReviewCardFooter = ({ familyMembersToReview = [], onSave, onAddAnotherPerson }) => {
  const [t] = useTranslation()
  const hasNewPeople = familyMembersToReview.length > 0

  return (
    <>
      {hasNewPeople && (
        <Button
          emphasis="high"
          onClick={() => onSave('continue')}
          fullWidth
          data-testid="review-card-save-continue-button"
          linkName="Review Card Save and Continue"
        >
          {t('review-card.footer.save-and-continue', 'Save and Continue')}
        </Button>
      )}
      {!hasNewPeople && (
        <>
          <Button
            emphasis="high"
            onClick={() => onSave('attach')}
            fullWidth
            data-testid="review-card-save-attach-button"
            linkName="Review Card Attach to Tree"
          >
            {t('review-card.footer.attach-to-tree', 'Attach to Tree')}
          </Button>
          <>
            <Separator size="xxs" />
            <Button
              emphasis="medium"
              onClick={onAddAnotherPerson}
              fullWidth
              data-testid="review-card-add-household-member-button"
              linkName="Review Card Add Household Member"
            >
              {t('review-card.footer.add-household-member', 'Add Household Member')}
            </Button>
          </>
          <Separator size="xxs" />
          <Button
            emphasis="medium"
            onClick={() => onSave('close')}
            fullWidth
            data-testid="review-card-save-close-button"
            linkName="Review Card Close"
          >
            {t('common-ui:close.action', 'Close')}
          </Button>
        </>
      )}
    </>
  )
}

ReviewCardFooter.propTypes = {
  familyMembersToReview: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      localizedName: PropTypes.string.isRequired,
    })
  ),
  onSave: PropTypes.func.isRequired,
  onAddAnotherPerson: PropTypes.func.isRequired,
}

ReviewCardFooter.defaultProps = {
  familyMembersToReview: [],
}

export default ReviewCardFooter
